import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDepartments, useAvailableTeamLeaders, useAvailableAgents, type Department } from "@/hooks/useConfig";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Loader2, ChevronRight, Search, X, Check } from "lucide-react";
import { toast } from "sonner";
import { DepartmentMember } from "@/lib/types";
import { cn } from "@/lib/utils";

function getDisplayName(m: DepartmentMember & { name?: string; profile?: { name?: string } }) {
  const name =
    m.profile?.name ??
    (m.profile as { name?: string } | undefined)?.name ??
    (m as { name?: string }).name;
  return name || m.email || m.id;
}

/** Leader from API may be flat { id, email, profile } or nested { user: { id, email, profile } } */
function normalizeLeader(leader: DepartmentMember | { user?: DepartmentMember }): DepartmentMember {
  return ((leader as { user?: DepartmentMember }).user ?? leader) as DepartmentMember;
}

function getLeaderDisplayName(leader: DepartmentMember | { user?: DepartmentMember }) {
  return getDisplayName(normalizeLeader(leader));
}

export function DepartmentManagement() {
  const navigate = useNavigate();
  const {
    departments,
    isLoading,
    createDepartment,
    updateDepartment,
    deleteDepartment,
  } = useDepartments();
  const { data: availableLeaders = [], isLoading: loadingLeaders } = useAvailableTeamLeaders();
  const { data: availableAgents = [], isLoading: loadingAgents } = useAvailableAgents();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const [name, setName] = useState("");
  const [teamLeaderIds, setTeamLeaderIds] = useState<string[]>([]);
  const [agentIds, setAgentIds] = useState<string[]>([]);
  const [editingDepartment, setEditingDepartment] = useState<Department | null>(null);
  const [deletingDepartment, setDeletingDepartment] = useState<Department | null>(null);

  // When create dialog opens, reset and optionally prefetch
  useEffect(() => {
    if (isCreateOpen) {
      setName("");
      setTeamLeaderIds([]);
      setAgentIds([]);
    }
  }, [isCreateOpen]);

  // When edit dialog opens, set current department's leader and agents (API returns nested { user } shape)
  useEffect(() => {
    if (isEditOpen && editingDepartment) {
      setName(editingDepartment.name);
      const leaders = editingDepartment.teamLeaders ?? (editingDepartment.teamLeader ? [editingDepartment.teamLeader] : []);
      setTeamLeaderIds(leaders.map((l) => normalizeLeader(l).id));
      setAgentIds(editingDepartment.agents?.map((a) => a.id) ?? []);
    }
  }, [isEditOpen, editingDepartment]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || name.trim().length < 2) {
      toast.error("Name must be at least 2 characters");
      return;
    }
    createDepartment.mutate(
      {
        name: name.trim(),
        ...(teamLeaderIds.length > 0 && { teamLeaderIds }),
        ...(agentIds.length > 0 && { agentsIds: agentIds }),
      },
      {
        onSuccess: () => {
          setName("");
          setTeamLeaderIds([]);
          setAgentIds([]);
          setIsCreateOpen(false);
        },
      }
    );
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingDepartment) return;
    if (!name || name.trim().length < 2) {
      toast.error("Name must be at least 2 characters");
      return;
    }
    updateDepartment.mutate(
      {
        id: editingDepartment.id,
        data: {
          name: name.trim(),
          teamLeaderIds: teamLeaderIds,
          agentsIds: agentIds,
        },
      },
      {
        onSuccess: () => {
          setEditingDepartment(null);
          setIsEditOpen(false);
        },
      }
    );
  };

  const handleDelete = async () => {
    if (!deletingDepartment) return;
    deleteDepartment.mutate(deletingDepartment.id, {
      onSuccess: () => {
        setDeletingDepartment(null);
        setIsDeleteOpen(false);
      },
    });
  };

  const openEditDialog = (department: Department) => {
    setEditingDepartment(department);
    setIsEditOpen(true);
  };

  const openDeleteDialog = (department: Department) => {
    setDeletingDepartment(department);
    setIsDeleteOpen(true);
  };

  const createdDate = (d: Department) =>
    (d as { createdAt?: string; created_at?: string }).createdAt ??
    (d as { created_at?: string }).created_at;

  // For edit: combined leader options = current (normalized from API) + available
  const rawCurrentLeaders = editingDepartment?.teamLeaders ?? (editingDepartment?.teamLeader ? [editingDepartment.teamLeader] : []);
  const currentLeadersFlat = rawCurrentLeaders.map(normalizeLeader);
  const currentLeaderIds = new Set(currentLeadersFlat.map((l) => l.id));
  const editLeaderOptions: DepartmentMember[] = [
    ...currentLeadersFlat,
    ...availableLeaders.filter((l) => !currentLeaderIds.has(l.id)),
  ];

  // For edit: combined agent options = current + available
  const currentAgentIds = new Set(editingDepartment?.agents?.map((a) => a.id) ?? []);
  const editAgentOptions: DepartmentMember[] = [
    ...(editingDepartment?.agents ?? []),
    ...availableAgents.filter((a) => !currentAgentIds.has(a.id)),
  ];

  const toggleAgent = (id: string, checked: boolean) => {
    setAgentIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const toggleLeader = (id: string, checked: boolean) => {
    setTeamLeaderIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogTrigger asChild>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Department
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Add Department</DialogTitle>
              <DialogDescription>
                Create a new department and optionally assign a team leader and agents.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Department Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., IT Support"
                  className="mt-1"
                  autoFocus
                />
              </div>
              <div>
                <Label>Team Leaders (optional)</Label>
                <div className="mt-1">
                  {loadingLeaders ? (
                    <p className="text-sm text-muted-foreground py-2">Loading team leaders…</p>
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between h-auto min-h-10 bg-background hover:bg-background/80"
                        >
                          <div className="flex flex-wrap gap-1 p-1">
                            {teamLeaderIds.length > 0 ? (
                              teamLeaderIds.map((id) => {
                                const m = availableLeaders.find((l) => l.id === id);
                                return (
                                  <Badge key={id} variant="secondary" className="flex items-center gap-1">
                                    <span
                                      className="flex items-center gap-1"
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleLeader(id, false); }}
                                      onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                    >
                                      {m ? getDisplayName(m) : id}
                                      <X className="h-3 w-3 cursor-pointer" />
                                    </span>
                                  </Badge>
                                );
                              })
                            ) : (
                              <span className="text-muted-foreground text-sm ml-1">Search and select team leaders…</span>
                            )}
                          </div>
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search team leaders..." />
                          <CommandList className="max-h-60">
                            <CommandEmpty>No team leaders found.</CommandEmpty>
                            <CommandGroup>
                              {availableLeaders.map((l) => (
                                <CommandItem
                                  key={l.id}
                                  onSelect={() => toggleLeader(l.id, !teamLeaderIds.includes(l.id))}
                                  className="flex items-center gap-2 cursor-pointer"
                                >
                                  <div
                                    className={cn(
                                      "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                      teamLeaderIds.includes(l.id) ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible"
                                    )}
                                  >
                                    <Check className="h-3 w-3" />
                                  </div>
                                  <span className="flex-1">{getDisplayName(l)}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </div>
              <div>
                <Label>Agents (optional)</Label>
                <div className="mt-1">
                  {loadingAgents ? (
                    <p className="text-sm text-muted-foreground py-2">Loading agents…</p>
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between h-auto min-h-10 bg-background hover:bg-background/80"
                        >
                          <div className="flex flex-wrap gap-1 p-1">
                            {agentIds.length > 0 ? (
                              agentIds.map((id) => {
                                const m = availableAgents.find((a) => a.id === id);
                                return (
                                  <Badge key={id} variant="secondary" className="flex items-center gap-1">
                                    <span
                                      className="flex items-center gap-1"
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleAgent(id, false); }}
                                      onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                    >
                                      {m ? getDisplayName(m) : id}
                                      <X className="h-3 w-3 cursor-pointer" />
                                    </span>
                                  </Badge>
                                );
                              })
                            ) : (
                              <span className="text-muted-foreground text-sm ml-1">Search and select agents…</span>
                            )}
                          </div>
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search agents..." />
                          <CommandList className="max-h-60">
                            <CommandEmpty>No agents found.</CommandEmpty>
                            <CommandGroup>
                              {availableAgents.map((a) => (
                                <CommandItem
                                  key={a.id}
                                  onSelect={() => toggleAgent(a.id, !agentIds.includes(a.id))}
                                  className="flex items-center gap-2 cursor-pointer"
                                >
                                  <div
                                    className={cn(
                                      "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                      agentIds.includes(a.id) ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible"
                                    )}
                                  >
                                    <Check className="h-3 w-3" />
                                  </div>
                                  <span className="flex-1">{getDisplayName(a)}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createDepartment.isPending}>
                {createDepartment.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-md">
          <form onSubmit={handleEdit}>
            <DialogHeader>
              <DialogTitle>Edit Department</DialogTitle>
              <DialogDescription>
                Update name, team leader, and agents.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-name">Department Name *</Label>
                <Input
                  id="edit-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1"
                  autoFocus
                />
              </div>
              <div>
                <Label>Team Leaders</Label>
                <div className="mt-1">
                  {editLeaderOptions.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">No team leaders to assign.</p>
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between h-auto min-h-10 bg-background hover:bg-background/80"
                        >
                          <div className="flex flex-wrap gap-1 p-1">
                            {teamLeaderIds.length > 0 ? (
                              teamLeaderIds.map((id) => {
                                const m = editLeaderOptions.find((l) => l.id === id);
                                return (
                                  <Badge key={id} variant="secondary" className="flex items-center gap-1">
                                    <span
                                      className="flex items-center gap-1"
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleLeader(id, false); }}
                                      onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                    >
                                      {m ? getDisplayName(m) : id}
                                      <X className="h-3 w-3 cursor-pointer" />
                                    </span>
                                  </Badge>
                                );
                              })
                            ) : (
                              <span className="text-muted-foreground text-sm ml-1">Search and select team leaders…</span>
                            )}
                          </div>
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search team leaders..." />
                          <CommandList className="max-h-60">
                            <CommandEmpty>No team leaders found.</CommandEmpty>
                            <CommandGroup>
                              {editLeaderOptions.map((l) => (
                                <CommandItem
                                  key={l.id}
                                  onSelect={() => toggleLeader(l.id, !teamLeaderIds.includes(l.id))}
                                  className="flex items-center gap-2 cursor-pointer"
                                >
                                  <div
                                    className={cn(
                                      "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                      teamLeaderIds.includes(l.id) ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible"
                                    )}
                                  >
                                    <Check className="h-3 w-3" />
                                  </div>
                                  <span className="flex-1">{getDisplayName(l)}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </div>
              <div>
                <Label>Agents</Label>
                <div className="mt-1">
                  {editAgentOptions.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">No agents to assign.</p>
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="w-full justify-between h-auto min-h-10 bg-background hover:bg-background/80"
                        >
                          <div className="flex flex-wrap gap-1 p-1">
                            {agentIds.length > 0 ? (
                              agentIds.map((id) => {
                                const m = editAgentOptions.find((a) => a.id === id);
                                return (
                                  <Badge key={id} variant="secondary" className="flex items-center gap-1">
                                    <span
                                      className="flex items-center gap-1"
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleAgent(id, false); }}
                                      onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                    >
                                      {m ? getDisplayName(m) : id}
                                      <X className="h-3 w-3 cursor-pointer" />
                                    </span>
                                  </Badge>
                                );
                              })
                            ) : (
                              <span className="text-muted-foreground text-sm ml-1">Search and select agents…</span>
                            )}
                          </div>
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search agents..." />
                          <CommandList className="max-h-60">
                            <CommandEmpty>No agents found.</CommandEmpty>
                            <CommandGroup>
                              {editAgentOptions.map((a) => (
                                <CommandItem
                                  key={a.id}
                                  onSelect={() => toggleAgent(a.id, !agentIds.includes(a.id))}
                                  className="flex items-center gap-2 cursor-pointer"
                                >
                                  <div
                                    className={cn(
                                      "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                      agentIds.includes(a.id) ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible"
                                    )}
                                  >
                                    <Check className="h-3 w-3" />
                                  </div>
                                  <span className="flex-1">{getDisplayName(a)}</span>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setEditingDepartment(null); setIsEditOpen(false); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateDepartment.isPending}>
                {updateDepartment.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Update
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete &quot;{deletingDepartment?.name}&quot;. This action cannot be undone. If there are
              tickets associated with this department, the deletion will fail.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingDepartment(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteDepartment.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Team Leaders</TableHead>
              <TableHead>Agents</TableHead>
              <TableHead>Tickets</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {departments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No departments yet. Create one to get started.
                </TableCell>
              </TableRow>
            ) : (
              departments.map((department) => (
                <TableRow
                  key={department.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/admin/departments/${department.id}`)}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-1">
                      <span>{department.name}</span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {(() => {
                      const leaders = department.teamLeaders ?? (department.teamLeader ? [department.teamLeader] : []);
                      return leaders.length > 0 ? leaders.map((l) => getLeaderDisplayName(l)).join(", ") : "—";
                    })()}
                  </TableCell>
                  <TableCell>{department.agents?.length ?? 0}</TableCell>
                  <TableCell>{department.ticketCount ?? 0}</TableCell>
                  <TableCell>
                    {createdDate(department)
                      ? new Date(createdDate(department)).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(department)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openDeleteDialog(department)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
